import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import {
  DisabledReason,
  FormValueControl,
  ValidationError,
  WithOptionalField,
} from '@angular/forms/signals';
import { SizeProp } from '@fortawesome/angular-fontawesome';
import { match } from 'ts-pattern';
import { IconComponent } from '../icon/icon.component';
import type { PsUiSize } from '../../types';

const SIZE_MAP: Record<PsUiSize, string> = {
  sm: 'form-control-sm',
  md: 'form-control-md',
  lg: 'form-control-lg',
};

@Component({
  selector: 'ps-password-input',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './password-input.component.html',
  styleUrl: './password-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordInputComponent implements FormValueControl<string> {
  // Core state
  public readonly value = model<string>('');
  public readonly touched = model<boolean>(false);

  // Availability state
  public readonly readonly = input<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly disabledReasons = input<
    readonly WithOptionalField<DisabledReason>[]
  >([]);

  // Validation state
  public readonly invalid = input<boolean>(false);
  public readonly errors = input<readonly WithOptionalField<ValidationError>[]>(
    [],
  );

  // Constraints
  public readonly required = input<boolean>(false);
  public readonly maxLength = input<number>();
  public readonly minLength = input<number | undefined>(0);

  // Identity
  public readonly name = input.required<string>();
  public readonly id = input.required<string>();

  // Component-specific properties
  public readonly size = input<PsUiSize>('md');
  public readonly label = input<string>('Password');
  public readonly placeholder = input<string>('Enter your password');

  public showPassword = signal<boolean>(false);
  protected readonly type = computed(() =>
    this.showPassword() ? 'text' : 'password',
  );
  protected readonly iconSize = computed(() => this.size() as SizeProp);
  protected readonly iconName = computed<string>(() =>
    this.showPassword() ? 'eye-slash' : 'eye',
  );

  protected readonly ariaLabel = computed<string>(() =>
    this.showPassword() ? 'Hide password' : 'Show password',
  );
  protected readonly ariaDescribedBy = computed<string>(
    () => `password-input-${this.id()}-errors`,
  );

  protected readonly isInvalid = computed(() => {
    return match({
      value: this.value(),
      invalid: this.invalid(),
      touched: this.touched(),
      hasErrors: this.errors().length > 0,
    })
      .with({ touched: true, invalid: true }, () => true)
      .with({ touched: true, hasErrors: true }, () => true)
      .otherwise(() => false);
  });

  protected readonly classes = computed(() => {
    return [
      'form-control',
      SIZE_MAP[this.size()],
      this.isInvalid() ? 'is-invalid' : '',
    ];
  });

  protected onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.value.set(inputElement.value);
  }

  protected onBlur(): void {
    this.touched.set(true);
  }

  protected toggleVisibility(): void {
    if (!this.disabled()) {
      this.showPassword.update((visible) => !visible);
    }
  }
}
